import React from "react";
import { Message } from "@/types/index";
import { WebSocketMessage } from "@/types/websocket";
import { cn } from "@/lib/utils";

interface MessageBubbleProps {
  /**
   * Mensagem a ser renderizada
   */
  message: Message | WebSocketMessage;

  /**
   * Se a mensagem é do usuário atual
   */
  isUser?: boolean;

  /**
   * Classe CSS adicional
   */
  className?: string;

  /**
   * Variante do componente
   */
  variant?: "default" | "compact" | "detailed";

  /**
   * Mostrar timestamp
   */
  showTimestamp?: boolean;

  /**
   * Mostrar nome do remetente
   */
  showSender?: boolean;

  /**
   * Formato do timestamp
   */
  timestampFormat?: "time" | "datetime" | "relative";

  /**
   * Callback executado ao clicar na mensagem
   */
  onClick?: (message: Message | WebSocketMessage) => void;

  /**
   * Callback executado ao clicar com botão direito
   */
  onContextMenu?: (
    message: Message | WebSocketMessage,
    event: React.MouseEvent,
  ) => void;
}

/**
 * Componente para renderizar uma mensagem individual
 *
 * @example
 * ```typescript
 * <MessageBubble
 *   message={message}
 *   isUser={message.role === 'user'}
 *   variant="detailed"
 *   showTimestamp={true}
 *   onClick={(msg) => console.log('Mensagem clicada:', msg)}
 * />
 * ```
 */
export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isUser = false,
  className = "",
  variant = "default",
  showTimestamp = true,
  showSender = true,
  timestampFormat = "time",
  onClick,
  onContextMenu,
}) => {
  // Extrai dados da mensagem
  const content = message.content || "";
  const timestamp = message.timestamp;
  const sender = "sender" in message ? message.sender : null;
  const role = "role" in message ? message.role : sender?.type || "assistant";

  // Determina se é usuário baseado em múltiplos critérios
  const isUserMessage = (() => {
    // 1. Se isUser foi explicitamente passado como true
    if (isUser) return true;
    
    // 2. Verificar role
    if (role === "user") return true;
    
    // 3. Verificar sender.type
    if (sender?.type === "user") return true;
    
    // 4. Verificar sender.id (se contém 'user' ou é um UUID de usuário)
    if (sender?.id && (
      sender.id === "user" || 
      sender.id.includes("user") || 
      sender.id.includes("User")
    )) return true;
    
    // 5. Verificar se não é assistente (fallback)
    if (role === "assistant" || sender?.type === "assistant") return false;
    
    // 6. Verificar sender.id para assistente
    if (sender?.id && (
      sender.id === "assistant" || 
      sender.id.includes("assistant") || 
      sender.id.includes("Assistant") ||
      sender.id.includes("AI") ||
      sender.id.includes("ai")
    )) return false;
    
    // 7. Por padrão, se não conseguimos identificar, assumir que é usuário
    // (mais seguro do que assumir que é assistente)
    return true;
  })();

  // Formata timestamp
  const formatTimestamp = (timestamp: string | number | undefined) => {
    if (!timestamp) return "-";

    let dateObj: Date;
    if (typeof timestamp === "number") {
      dateObj = new Date(timestamp);
    } else {
      dateObj = new Date(timestamp);
    }

    if (isNaN(dateObj.getTime())) return "-";

    switch (timestampFormat) {
      case "time":
        return dateObj.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        });
      case "datetime":
        return dateObj.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      case "relative":
        const now = new Date();
        const diff = now.getTime() - dateObj.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return "Agora";
        if (minutes < 60) return `${minutes}m atrás`;
        if (hours < 24) return `${hours}h atrás`;
        if (days < 7) return `${days}d atrás`;
        return dateObj.toLocaleDateString("pt-BR");
      default:
        return dateObj.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        });
    }
  };

  // Obtém nome do remetente
  const senderName =
    typeof message.sender === "string"
      ? message.sender
      : message.sender?.name || message.sender?.id || "Unknown";

  // Classes Tailwind baseadas na variante
  const getVariantClasses = () => {
    switch (variant) {
      case "compact": {
        return "p-2 px-3 my-1 text-sm";
      }
      case "detailed": {
        return "p-4 my-2 text-base";
      }
      default: {
        return "p-3 px-4 my-1.5 text-sm";
      }
    }
  };

  const variantClasses = getVariantClasses();

  // Classes base para o container da mensagem
  const messageClasses = cn(
    // Classes base
    "max-w-[80%] transition-all duration-200 ease-in-out break-words whitespace-pre-wrap relative",
    // Variante
    variantClasses,
    // Posicionamento baseado no tipo de mensagem
    isUserMessage ? "self-end" : "self-start",
    // Cores e estilos baseados no tipo de mensagem
    isUserMessage 
      ? "bg-blue-500/20 text-platinum border border-blue-500/30 backdrop-blur-sm" 
      : "glass-card text-platinum border-white/10",
    // Bordas arredondadas baseadas no tipo de mensagem
    isUserMessage 
      ? "rounded-2xl rounded-br-md" 
      : "rounded-2xl rounded-bl-md",
    // Cursor e hover
    onClick ? "cursor-pointer hover:shadow-lg hover:border-white/20" : "cursor-default",
    // Classe adicional
    className
  );

  return (
    <div className="flex items-start gap-3">
      {/* Avatar */}
      {!isUserMessage && (
        <div className="shrink-0 w-8 h-8 rounded-full gradient-primary-br flex items-center justify-center shadow-lg shadow-blue-500/20">
          <span className="text-white text-sm font-bold">AI</span>
        </div>
      )}
      
      <div
        className={messageClasses}
        onClick={() => onClick?.(message)}
        onContextMenu={(e) => onContextMenu?.(message, e)}
        role="article"
        aria-label={`Mensagem de ${senderName} (${isUserMessage ? 'usuário' : 'assistente'})`}
        tabIndex={0}
      >
        {showSender && (
          <div className={cn(
            "font-semibold mb-2 text-xs flex items-center gap-1",
            isUserMessage ? "text-blue-300" : "text-silver"
          )}>
            {senderName}
            {!isUserMessage && " · Assistente IA"}
          </div>
        )}

        <div className={cn(
          "leading-relaxed",
          variant === "compact" ? "text-sm" : 
          variant === "detailed" ? "text-base" : "text-sm"
        )}>
          <p data-testid="message-content" className="whitespace-pre-wrap break-words m-0">
            {content || ''}
          </p>
        </div>

        {showTimestamp && (
          <div className={cn(
            "block mt-2 text-xs",
            isUserMessage ? "text-blue-300/60" : "text-silver/60"
          )}>
            {formatTimestamp(timestamp)}
          </div>
        )}
      </div>
      {/* User Avatar */}
      {isUserMessage && (
        <div className="shrink-0 w-8 h-8 rounded-full bg-blue-500/30 border border-blue-500/50 flex items-center justify-center">
          <span className="text-blue-300 text-sm font-bold">U</span>
        </div>
      )}
    </div>
  );
};

/**
 * Componente especializado para mensagens de chat
 */
export const ChatMessageBubble: React.FC<
  Omit<MessageBubbleProps, "message"> & { message: Message }
> = (props) => {
  return <MessageBubble {...props} />;
};

/**
 * Componente especializado para mensagens WebSocket
 */
export const WebSocketMessageBubble: React.FC<
  Omit<MessageBubbleProps, "message"> & { message: WebSocketMessage }
> = (props) => {
  return <MessageBubble {...props} />;
};
